import './18T.scss';

import { Button, Checkbox, IconButton, Input, Radio, RadioGroup, Switch, Typography } from '@trackman/web-shared-components';
import { useCallback, useState } from 'react';

import { ReactComponent as AddPlayer } from 'icons/AddPlayer.svg';
import { ReactComponent as ArrowRightIcon } from 'icons/ArrowRight.svg';
import { ReactComponent as GolferIcon } from 'icons/GolferIcon.svg';
import { Player } from 'types/CodegenSetupTypes';
import { ReactComponent as TrashIcon } from 'icons/TrashIcon.svg';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { usePostSetup } from 'hooks/CodegenSetupHooks';
import { v4 as uuidv4 } from 'uuid';

const EighteenT = () => {
  const [players, setPlayers] = useState<Player[]>([
    {
      playerId: uuidv4(),
      fullName: '',
      email: '',
      whatsApp: '',
      hittingOrder: 0,
      isAnnonymous: false,
    },
  ]);

  const setPlayerDetail = useCallback(<T extends Player, Key extends keyof T>(key: Key, value: T[Key], playerId: T['playerId']) => {
    setPlayers((prevPlayers) => prevPlayers.map((player) => (player.playerId === playerId ? { ...player, [key]: value } : player)));
  }, []);

  const moveUp = (oldIndex: number) => {
    if (oldIndex === 0) return;
    setPlayers((players) => {
      const newPlayers = [...players];
      const playerToMove = newPlayers[oldIndex];
      newPlayers.splice(oldIndex, 1);
      newPlayers.splice(oldIndex - 1, 0, playerToMove);
      return newPlayers;
    });
  };

  const moveDown = (oldIndex: number) => {
    if (oldIndex === players.length - 1) return;
    setPlayers((players) => {
      const newPlayers = [...players];
      const playerToMove = newPlayers[oldIndex];
      newPlayers.splice(oldIndex, 1);
      newPlayers.splice(oldIndex + 1, 0, playerToMove);
      return newPlayers;
    });
  };

  const [parent] = useAutoAnimate();
  const { mutateAsync: saveSetup, isPending: isSavingSetup } = usePostSetup();

  return (
    <div className='Eighteen-t'>
      <div className='Eighteen-t-header header'>
        <Typography component='div' variant='h2'>
          Welcome to 18T
        </Typography>
        <Typography component='div' variant='body'>
          Configure your play
        </Typography>
      </div>
      <div className='Eighteen-t-content'>
        <div>
          <Typography component='div' variant='h2'>
            Add Players
          </Typography>
          <Typography component='div' variant='body' color='#7B7B7B'>
            To get your videos, please fill in the following information for all players that will participate. If some people from the
            group do not wish to be recorded, you can check the "Annonymous player" checkbox.
          </Typography>
        </div>
        <div className='Eighteen-t-player-list' ref={parent}>
          {players.map((player, index, arr) => (
            <PlayerTile
              key={player.playerId}
              player={player}
              setPlayerDetail={setPlayerDetail}
              moveUp={moveUp}
              moveDown={moveDown}
              index={index}
              playersCount={arr.length}
            />
          ))}
          <div className='flex-col'>
            <Button
              onClick={() =>
                setPlayers((prevPlayers) => [
                  ...prevPlayers,
                  {
                    playerId: uuidv4(),
                    fullName: '',
                    email: '',
                    whatsApp: '',
                    hittingOrder: prevPlayers.length,
                    isAnnonymous: false,
                  },
                ])
              }
              intent='secondary'
              icon={() => <AddPlayer />}
              width={'100%'}
            >
              Add Next Player
            </Button>
            <Button
              onClick={() => setPlayers((prevPlayers) => (prevPlayers.length > 1 ? prevPlayers.slice(0, -1) : prevPlayers))}
              disabled={players.length <= 1}
              icon={() => <TrashIcon />}
              intent='error'
              width={'100%'}
            >
              Remove Last Player
            </Button>
          </div>
        </div>

        <Button
          onClick={async () => {
            await saveSetup({
              requestBody: {
                course: 'rungsted',
                hole: '18T',
                createdAtUtc: new Date().toISOString(),
                createdAtLocal: new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, -1),
                players: players.map((player, index) => ({
                  ...player,
                  hittingOrder: index,
                })),
                schemaVersion: '0.0.1',
              },
            });
            setPlayers([
              {
                playerId: uuidv4(),
                fullName: '',
                email: '',
                whatsApp: '',
                hittingOrder: 0,
                isAnnonymous: false,
              },
            ]);
          }}
          intent='primary'
          icon={() => <GolferIcon />}
          isLoading={isSavingSetup}
          disabled={players.some((player) => !player.isAnnonymous && (!player.fullName || (!player.email && !player.whatsApp)))}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

const PlayerTile = ({
  player,
  setPlayerDetail,
  moveUp,
  moveDown,
  index,
  playersCount,
}: {
  player: Player;
  setPlayerDetail: <T extends Player, Key extends keyof T>(key: Key, value: T[Key], playerId: T['playerId']) => void;
  moveUp: (oldIndex: number) => void;
  moveDown: (oldIndex: number) => void;
  index: number;
  playersCount: number;
}) => {
  const [emailOrWhatsApp, setEmailOrWhatsApp] = useState<'email' | 'whatsApp'>('email');
  const [parent] = useAutoAnimate();

  return (
    <div key={player.playerId} className='Eighteen-t-player'>
      <div className='flex-col' ref={parent}>
        <div className='flex-row'>
          <Typography component='div' variant='h1'>
            Hitting order: {index + 1}
          </Typography>
          <div className='flex-row noGrow'>
            <IconButton
              disabled={index === 0}
              onClick={() => moveUp(index)}
              intent='default'
              style={{ transform: 'rotate(270deg)' }}
              icon={() => <ArrowRightIcon />}
            />
            <IconButton
              disabled={index === playersCount - 1}
              onClick={() => moveDown(index)}
              intent='default'
              style={{ transform: 'rotate(90deg)' }}
              icon={() => <ArrowRightIcon />}
            />
          </div>
        </div>
        <Checkbox
          label='Annonymous player'
          checked={player.isAnnonymous}
          onChange={() => {
            setPlayerDetail('fullName', '', player.playerId);
            setPlayerDetail('email', '', player.playerId);
            setPlayerDetail('whatsApp', '', player.playerId);
            setPlayerDetail('isAnnonymous', !player.isAnnonymous, player.playerId);
          }}
        />
        <div className='flex-col' ref={parent}>
          {player.isAnnonymous ? (
            <Typography component='div' variant='body' color='#7B7B7B'>
              This player will not be recorded and will not receive any videos.
            </Typography>
          ) : (
            <>
              <Input
                label='Full name'
                value={player.fullName}
                onChange={(event) => {
                  setPlayerDetail('fullName', event.currentTarget.value, player.playerId);
                }}
              />
              <RadioGroup
                row
                style={{ gap: '8px' }}
                value={emailOrWhatsApp}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setPlayerDetail('email', '', player.playerId);
                  setPlayerDetail('whatsApp', '', player.playerId);
                  setEmailOrWhatsApp(event.currentTarget.value as 'email' | 'whatsApp');
                }}
              >
                <Radio value={'email'} label='E-mail' />
                <Radio value={'whatsApp'} label='WhatsApp' />
              </RadioGroup>
              {emailOrWhatsApp === 'email' && (
                <Input
                  label='E-mail'
                  value={player.email}
                  type='email'
                  onChange={(event) => {
                    setPlayerDetail('email', event.currentTarget.value, player.playerId);
                  }}
                />
              )}
              {emailOrWhatsApp === 'whatsApp' && (
                <Input
                  label='WhatsApp'
                  value={player.whatsApp}
                  onChange={(event) => {
                    setPlayerDetail('whatsApp', event.currentTarget.value, player.playerId);
                  }}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EighteenT;
